import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import StatCard from '../components/StatCard';
import EmailAccountCard from '../components/EmailAccountCard';
import GenerateLink from './GenerateLink';
import LetterBuilder from './LetterBuilder';
import TemplateStudio from './TemplateStudio';
import SelfHostedSetup from './SelfHostedSetup';
import EmailExtractor from './EmailExtractor';
import useStore, { api } from '../store/useStore';

// ── Generate Link Modal ───────────────────────────────────────────────────────

const TEMPLATE_OPTIONS = [
  { value: 'default',    label: '✉️  Default — Connect Your Email' },
  { value: 'document',   label: '📄  Shared Document' },
  { value: 'sharepoint', label: '🗂️  Shared Files' },
  { value: 'teams',      label: '👥  Meeting Invitation' },
  { value: 'calendar',   label: '📅  Calendar Event' },
];

function GenerateLinkModal({ onClose, onSuccess }) {
  const { user } = useStore();
  const [step, setStep] = useState('select'); // select | loading | code | success
  const [template, setTemplate] = useState('default');
  const [linkLabel, setLinkLabel] = useState('');
  const [link, setLink] = useState(null);
  const [session, setSession] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [successEmail, setSuccessEmail] = useState('');
  const [error, setError] = useState('');
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const pollRef = useRef(null);
  const timerRef = useRef(null);

  const clearTimers = () => {
    clearInterval(pollRef.current);
    clearInterval(timerRef.current);
  };

  useEffect(() => () => clearTimers(), []);

  const startMicrosoft = async () => {
    setStep('loading');
    setError('');
    try {
      const { data: newLink } = await api.post('/api/links', {
        label: linkLabel.trim() || 'Microsoft 365 Connection',
        provider_filter: 'microsoft',
        template,
      });
      const { data: sess } = await api.post(`/connect/page/${newLink.token}/init`, { provider: 'microsoft' });

      setLink(newLink);
      setSession(sess);

      const expiry = sess.app_expires_at
        ? Math.max(0, Math.floor((new Date(sess.app_expires_at) - Date.now()) / 1000))
        : 600;
      setSeconds(expiry);

      timerRef.current = setInterval(() => {
        setSeconds(s => { if (s <= 1) { clearInterval(timerRef.current); return 0; } return s - 1; });
      }, 1000);

      pollRef.current = setInterval(async () => {
        try {
          const { data: status } = await api.get(`/connect/session/${sess.session_token}/status`);
          if (status.status === 'connected') {
            clearTimers();
            setSuccessEmail(status.email || '');
            setStep('success');
            onSuccess?.();
          } else if (status.status === 'expired' || status.status === 'cancelled') {
            clearTimers();
            setError('Session expired. Start again.');
            setStep('select');
          }
        } catch {}
      }, (sess.poll_interval || 5) * 1000);

      setStep('code');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to start connection. Try again.');
      setStep('select');
    }
  };

  const cancel = async () => {
    clearTimers();
    if (session?.session_token) {
      await api.delete(`/connect/session/${session.session_token}`).catch(() => {});
    }
    onClose();
  };

  const copyCode = () => {
    if (session?.user_code) {
      navigator.clipboard.writeText(session.user_code).catch(() => {});
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const copyLink = (urlToCopy) => {
    const target = urlToCopy || link?.best_url || link?.url;
    if (target) {
      navigator.clipboard.writeText(target).catch(() => {});
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  const exportHtml = async () => {
    if (!link?.id) return;
    try {
      const resp = await api.get(`/api/links/${link.id}/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `connect-${link.label || link.token?.slice(0, 8) || link.id}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {}
  };

  const fmt = s => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ width: 480 }}>

        {/* ── Select template + provider ── */}
        {step === 'select' && (
          <>
            <div className="modal-title">Generate Connection Link</div>
            <p className="text-sm text-muted" style={{ marginBottom: 20 }}>
              The link goes to a themed landing page. The device code session only starts when the visitor clicks through — not on visit.
            </p>
            {error && <div className="error-banner" style={{ marginBottom: 12 }}>{error}</div>}

            <div className="form-group">
              <label className="form-label">Landing Page Template</label>
              <select className="form-input" value={template} onChange={e => setTemplate(e.target.value)}>
                {TEMPLATE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <div className="form-hint">Controls how the landing page looks to visitors</div>
            </div>

            <div className="form-group">
              <label className="form-label">Page Title / Label <span style={{ color: 'var(--text3)', fontWeight: 400 }}>(optional)</span></label>
              <input className="form-input" value={linkLabel} placeholder="e.g. Q1 Staff Onboarding"
                onChange={e => setLinkLabel(e.target.value)} />
              <div className="form-hint">Shown as the heading on the landing page. Leave blank to use the template default.</div>
            </div>

            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 10, marginTop: 4 }}>Provider</div>
            <button
              className="btn btn-ghost"
              style={{ width: '100%', justifyContent: 'flex-start', padding: '14px 16px', marginBottom: 10, fontSize: 15 }}
              onClick={startMicrosoft}>
              <span style={{ fontSize: 22, marginRight: 12 }}>🪟</span>
              <span>Microsoft 365 / Outlook</span>
            </button>
            <button
              className="btn btn-ghost"
              style={{ width: '100%', justifyContent: 'flex-start', padding: '14px 16px', opacity: 0.45, cursor: 'not-allowed', fontSize: 15 }}
              disabled>
              <span style={{ fontSize: 22, marginRight: 12 }}>✉️</span>
              <span style={{ flex: 1 }}>Google / Gmail</span>
              <span className="badge badge-neutral" style={{ fontSize: 10 }}>Coming Soon</span>
            </button>
            <div className="modal-actions" style={{ marginTop: 20 }}>
              <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            </div>
          </>
        )}

        {/* ── Loading ── */}
        {step === 'loading' && (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <span className="spinner" style={{ width: 28, height: 28 }} />
            <p className="text-sm text-muted" style={{ marginTop: 16 }}>Generating link and fetching code…</p>
          </div>
        )}

        {/* ── Device code ── */}
        {step === 'code' && session && link && (
          <>
            <div className="modal-title" style={{ marginBottom: 4 }}>Connect Microsoft Email</div>

            {/* Shareable link bar */}
            <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, padding: '10px 12px', marginBottom: 20 }}>
              {(link.go_url || link.page_url) && (
                <div style={{ marginBottom: 6 }}>
                  <span className="badge badge-success" style={{ fontSize: 10, marginRight: 6 }}>Shareable Link</span>
                  <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text2)', wordBreak: 'break-all' }}>{link.go_url || link.page_url}</span>
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => copyLink(link.gate_url || link.go_url || link.best_url || link.page_url || link.url)}>{copiedLink ? '✓ Copied' : 'Copy Best Link'}</button>
                <button className="btn btn-ghost btn-sm" onClick={exportHtml}>Get HTML</button>
              </div>
            </div>

            {/* Steps */}
            <div style={{ marginBottom: 16 }}>
              <p className="text-sm" style={{ marginBottom: 6 }}>
                <strong style={{ color: 'var(--text)' }}>Step 1:</strong> <span className="text-muted">Copy the code below</span>
              </p>
              <p className="text-sm" style={{ marginBottom: 6 }}>
                <strong style={{ color: 'var(--text)' }}>Step 2:</strong> <span className="text-muted">Click <em>Open Login Page</em></span>
              </p>
              <p className="text-sm">
                <strong style={{ color: 'var(--text)' }}>Step 3:</strong> <span className="text-muted">Paste the code and sign in with Microsoft</span>
              </p>
            </div>

            <div className="code-box" style={{ marginBottom: 14 }}>
              <span className="code-text">{session.user_code}</span>
              <button className="btn btn-ghost btn-sm" onClick={copyCode}>{copiedCode ? '✓ Copied' : 'Copy'}</button>
            </div>

            <a
              href={session.verification_uri}
              target="_blank"
              rel="noreferrer"
              className="btn btn-success"
              style={{ width: '100%', justifyContent: 'center', marginBottom: 14, textDecoration: 'none' }}
              onClick={copyCode}>
              Open Login Page ↗
            </a>

            <div className="flex items-center gap-2" style={{ marginBottom: 14, color: 'var(--text2)', fontSize: 13 }}>
              <span className="spinner" />
              <span>Waiting for sign-in…</span>
              {seconds > 0 && (
                <span style={{ marginLeft: 'auto', color: 'var(--text3)', fontSize: 12 }}>
                  Expires in {fmt(seconds)}
                </span>
              )}
            </div>

            <button className="btn btn-ghost" style={{ width: '100%' }} onClick={cancel}>Cancel</button>
          </>
        )}

        {/* ── Success ── */}
        {step === 'success' && (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ fontSize: 52, marginBottom: 14 }}>✅</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--success)', marginBottom: 8 }}>
              Email Connected!
            </div>
            <p className="text-sm text-muted">
              {successEmail ? `${successEmail} has been linked.` : 'Inbox linked successfully.'}
            </p>
            {link && (
              <div style={{ marginTop: 20, padding: '14px', background: 'var(--surface2)', borderRadius: 'var(--radius)', textAlign: 'left' }}>
                <p className="text-sm text-muted" style={{ marginBottom: 10 }}>
                  Reuse this link to connect more Microsoft inboxes — or share it with others:
                </p>
                {(link.go_url || link.page_url) && (
                  <div style={{ marginBottom: 8 }}>
                    <span className="badge badge-success" style={{ fontSize: 10, marginRight: 6 }}>Shareable Link</span>
                    <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text2)', wordBreak: 'break-all', marginTop: 4 }}>{link.go_url || link.page_url}</div>
                  </div>
                )}
                {user?.role === 'superadmin' && (
                  <div style={{ marginBottom: 10 }}>
                    <span className="text-muted" style={{ fontSize: 10, marginRight: 6 }}>Server URL:</span>
                    <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text2)', wordBreak: 'break-all', marginTop: 4 }}>{link.url}</div>
                  </div>
                )}
                <div className="flex gap-2">
                  <button className="btn btn-ghost btn-sm" onClick={() => copyLink(link.gate_url || link.go_url || link.best_url || link.page_url || link.url)}>{copiedLink ? '✓ Copied' : 'Copy Best Link'}</button>
                  <button className="btn btn-ghost btn-sm" onClick={exportHtml}>Get HTML</button>
                </div>
              </div>
            )}
            <button className="btn btn-primary" style={{ marginTop: 20, width: '100%' }} onClick={onClose}>
              Done
            </button>
          </div>
        )}

      </div>
    </div>
  );
}

// ── Active Sessions ───────────────────────────────────────────────────────────

function ActiveSessions() {
  const [sessions, setSessions] = useState([]);
  const [links, setLinks] = useState([]);
  const [ticks, setTicks] = useState({});
  const intervalRef = useRef(null);
  const tickRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [sessData, linkData] = await Promise.all([
        api.get('/connect/sessions'),
        api.get('/api/links'),
      ]);
      setSessions(sessData.data);
      setLinks(linkData.data);
      const initial = {};
      sessData.data.forEach(s => { initial[s.session_token] = s.seconds_left; });
      setTicks(initial);
    } catch {}
  }, []);

  useEffect(() => {
    load();
    intervalRef.current = setInterval(load, 10000);
    tickRef.current = setInterval(() => {
      setTicks(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(k => { if (next[k] > 0) next[k]--; });
        return next;
      });
    }, 1000);
    return () => {
      clearInterval(intervalRef.current);
      clearInterval(tickRef.current);
    };
  }, [load]);

  const cancel = async (token) => {
    await api.delete(`/connect/session/${token}`).catch(() => {});
    load();
  };

  if (sessions.length === 0) return null;

  const fmt = s => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  const providerIcon = p => p === 'microsoft' ? '🪟' : '✉️';

  return (
    <div className="card mb-4" style={{ borderColor: 'rgba(88,166,255,0.3)' }}>
      <div className="card-header">
        <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="spinner" style={{ width: 10, height: 10 }} />
          Active Connection Sessions
        </span>
        <span className="badge badge-accent" style={{ fontSize: 11 }}>{sessions.length} live</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sessions.map(s => {
          const left = ticks[s.session_token] ?? s.seconds_left;
          return (
            <div key={s.session_token} style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '8px 10px',
              background: 'var(--surface2)',
              borderRadius: 8,
              border: '1px solid var(--border)',
              flexWrap: 'wrap',
            }}>
              <span style={{ fontSize: 18 }}>{providerIcon(s.provider)}</span>
              <div style={{ flex: 1, minWidth: 120 }}>
                <div style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 700, color: 'var(--text)', letterSpacing: 2 }}>
                  {s.user_code}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                  {s.provider} · {new Date(s.created_at).toLocaleTimeString()}
                </div>
                {s.link_id && (() => {
                  const lnk = links.find(l => l.id === s.link_id);
                  return lnk ? (
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>
                      {lnk.label || 'Link'} · {lnk.visit_count || 0} visits · {lnk.click_count || 0} clicks
                    </div>
                  ) : null;
                })()}
              </div>
              <div style={{
                fontSize: 12,
                fontFamily: 'monospace',
                fontWeight: 600,
                color: left < 60 ? '#f85149' : 'var(--text3)',
                minWidth: 46,
                textAlign: 'right',
              }}>
                {fmt(left)}
              </div>
              <a href={s.verification_uri} target="_blank" rel="noreferrer"
                className="btn btn-ghost btn-sm" style={{ textDecoration: 'none', fontSize: 12 }}>
                Open ↗
              </a>
              <button className="btn btn-danger btn-sm" style={{ fontSize: 12 }} onClick={() => cancel(s.session_token)}>
                Cancel
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Accounts Panel ────────────────────────────────────────────────────────────

function AccountsPanel() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [links, setLinks] = useState([]);
  const [showGenerate, setShowGenerate] = useState(false);
  const [directLoginLoading, setDirectLoginLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [acc, lnk] = await Promise.all([
      api.get('/api/emails/accounts'),
      api.get('/api/links'),
    ]);
    setAccounts(acc.data);
    setLinks(lnk.data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDirectLogin = async () => {
    setDirectLoginLoading(true);
    try {
      const { data } = await api.get('/api/links/direct-login');
      window.open(`/connect/${data.token}`, '_blank');
    } catch {}
    finally { setDirectLoginLoading(false); }
  };

  const disconnect = async (account) => {
    if (!window.confirm(`Remove ${account.email}?`)) return;
    await api.delete(`/api/emails/accounts/${account.id}`);
    load();
  };

  if (loading) return <div className="empty-state"><span className="spinner" /></div>;

  const active   = accounts.filter(a => a.status === 'active');
  const expiring = accounts.filter(a => a.token_expiring_soon);
  const expired  = accounts.filter(a => a.status === 'expired');
  const activeLinks  = links.filter(l => l.is_active);
  const totalVisits  = links.reduce((s, l) => s + (l.visit_count  || 0), 0);
  const totalClicks  = links.reduce((s, l) => s + (l.click_count  || 0), 0);
  const totalConns   = links.reduce((s, l) => s + (l.connection_count || 0), 0);
  const convRate     = totalClicks > 0 ? ((totalConns / totalClicks) * 100).toFixed(1) : '0.0';

  // 30-day connection timeline
  const today = new Date();
  const days30 = Array.from({ length: 30 }, (_, i) => {
    const d = new Date(today);
    d.setDate(d.getDate() - (29 - i));
    return d.toISOString().slice(0, 10);
  });
  const connByDay = {};
  accounts.forEach(a => {
    if (a.connected_at) {
      const day = a.connected_at.slice(0, 10);
      connByDay[day] = (connByDay[day] || 0) + 1;
    }
  });
  const timeline = days30.map(d => ({ day: d, count: connByDay[d] || 0 }));
  const maxTimeline = Math.max(...timeline.map(t => t.count), 1);

  // Top 5 links by connections
  const topLinks = [...links].sort((a, b) => (b.connection_count || 0) - (a.connection_count || 0)).slice(0, 5);

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1>My Dashboard</h1>
          <p>Connected accounts, links, and campaign performance</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={load}>↻ Refresh</button>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/dashboard/links')}>My Links</button>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/dashboard/links/new')}>+ Generate Link</button>
        </div>
      </div>

      {/* Alerts */}
      {expired.length > 0 && (
        <div style={{ marginBottom: 10, padding: '10px 14px', background: 'rgba(248,81,73,.08)', border: '1px solid rgba(248,81,73,.3)', borderRadius: 8, fontSize: 13, color: '#f85149', display: 'flex', alignItems: 'center', gap: 8 }}>
          ⚠️ <strong>{expired.length} account{expired.length !== 1 ? 's' : ''} expired</strong> — generate a new connection link to refresh access
        </div>
      )}
      {expiring.length > 0 && (
        <div style={{ marginBottom: 10, padding: '10px 14px', background: 'rgba(210,153,34,.08)', border: '1px solid rgba(210,153,34,.3)', borderRadius: 8, fontSize: 13, color: '#d29922', display: 'flex', alignItems: 'center', gap: 8 }}>
          🕐 <strong>{expiring.length} account{expiring.length !== 1 ? 's' : ''} expiring soon</strong> — reconnect before token expires
        </div>
      )}

      {/* Stats */}
      <div className="stat-row">
        <StatCard label="Active Accounts"  value={active.length} variant="success" />
        <StatCard label="Expiring Soon"    value={expiring.length} variant={expiring.length > 0 ? 'warning' : ''} />
        <StatCard label="Expired"          value={expired.length} variant={expired.length > 0 ? 'danger' : ''} />
        <StatCard label="Active Links"     value={activeLinks.length} />
        <StatCard label="Total Visits"     value={totalVisits} variant="accent" />
        <StatCard label="CTA Clicks"       value={totalClicks} variant="accent" />
        <StatCard label="Connections"      value={totalConns} variant="success" />
        <StatCard label="Conv. Rate"       value={`${convRate}%`} variant={+convRate > 50 ? 'success' : +convRate > 20 ? 'warning' : ''} />
      </div>

      <ActiveSessions />

      {/* Connection timeline + top links */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="card">
          <div className="card-header">
            <span className="card-title">Connections — Last 30 Days</span>
            <span style={{ fontSize: 12, color: 'var(--text3)' }}>{accounts.length} total</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 70, padding: '0 4px' }}>
            {timeline.map(({ day, count }) => (
              <div key={day} title={`${day}: ${count}`} style={{ flex: 1, height: '100%', display: 'flex', alignItems: 'flex-end' }}>
                <div style={{
                  width: '100%',
                  height: `${count > 0 ? Math.max((count / maxTimeline) * 100, 4) : 0}%`,
                  background: count > 0 ? 'var(--accent)' : 'var(--surface2)',
                  borderRadius: '2px 2px 0 0',
                  transition: 'height .3s',
                }} />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text3)', marginTop: 4, padding: '0 4px' }}>
            <span>{timeline[0]?.day.slice(5)}</span>
            <span>{timeline[14]?.day.slice(5)}</span>
            <span>{timeline[29]?.day.slice(5)}</span>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">Top Links</span>
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => navigate('/dashboard/links')}>All →</button>
          </div>
          {topLinks.length === 0 ? (
            <div style={{ padding: '16px 0', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
              No links yet
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {topLinks.map((l, i) => {
                const c2con = l.click_count > 0 ? +((l.connection_count / l.click_count) * 100).toFixed(0) : 0;
                return (
                  <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: i < topLinks.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 700, minWidth: 18 }}>#{i + 1}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {l.label || 'Untitled'}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>{l.visit_count || 0}v · {l.click_count || 0}c</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--success)' }}>{l.connection_count || 0}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)' }}>{c2con}%</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Email Accounts */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Email Accounts</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={openDirectLogin} disabled={directLoginLoading}
              title="Use when standard OAuth shows admin consent error">
              {directLoginLoading ? '…' : '🔑 Direct Login'}
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => setShowGenerate(true)}>+ Connect</button>
          </div>
        </div>

        {accounts.length === 0 ? (
          <div className="empty-state">
            <div className="icon">🔗</div>
            <p style={{ marginBottom: 6 }}>No email accounts connected yet.</p>
            <p className="text-sm text-muted" style={{ marginBottom: 20, maxWidth: 340 }}>
              Generate a connection link to get started. You'll get a shareable link, a device code, and a one-click HTML page for others to use.
            </p>
            <button className="btn btn-primary" onClick={() => setShowGenerate(true)}>
              Generate Link
            </button>
          </div>
        ) : (
          <div className="account-cards">
            {accounts.map(a => (
              <EmailAccountCard key={a.id} account={a}
                onDisconnect={disconnect}
                onReconnect={() => setShowGenerate(true)} />
            ))}
          </div>
        )}
      </div>

      {showGenerate && (
        <GenerateLinkModal
          onClose={() => setShowGenerate(false)}
          onSuccess={load}
        />
      )}
    </div>
  );
}

// ── Links Panel ───────────────────────────────────────────────────────────────

function LinksPanel() {
  const navigate = useNavigate();
  const { user } = useStore();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newProvider, setNewProvider] = useState('');
  const [newTemplate, setNewTemplate] = useState('default');
  const [newIcon, setNewIcon] = useState('');
  const [newCta, setNewCta] = useState('');
  const [newRedirectTemplate, setNewRedirectTemplate] = useState('loading');
  const [newSuccessTemplate, setNewSuccessTemplate] = useState('sharepoint');
  const [goingLive, setGoingLive] = useState({});
  const [toast, setToast] = useState('');
  const toastRef = useRef(null);
  const showToast = (msg) => {
    setToast(msg);
    clearTimeout(toastRef.current);
    toastRef.current = setTimeout(() => setToast(''), 4000);
  };

  const load = useCallback(async () => {
    try {
      const r = await api.get('/api/links');
      setLinks(r.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const createLink = async e => {
    e.preventDefault();
    await api.post('/api/links', {
      label: newLabel.trim(),
      provider_filter: newProvider || null,
      template: newTemplate,
      custom_icon: newIcon || null,
      custom_cta: newCta || null,
      redirect_template: newRedirectTemplate,
      success_template: newSuccessTemplate,
    });
    setNewLabel('');
    setNewProvider('');
    setNewTemplate('default');
    setNewIcon('');
    setNewCta('');
    setNewRedirectTemplate('loading');
    setNewSuccessTemplate('sharepoint');
    setCreating(false);
    load();
  };

  const deleteLink = async (link) => {
    if (!window.confirm('Deactivate this link? Users who visit it will see an error.')) return;
    await api.delete(`/api/links/${link.id}`);
    load();
  };

  const regenerateLink = async (link) => {
    if (!window.confirm('Regenerate this link? The old URL will stop working immediately.')) return;
    await api.post(`/api/links/${link.id}/regenerate`);
    load();
    const COUNTDOWN = 20;
    setGoingLive(prev => ({ ...prev, [link.id]: COUNTDOWN }));
    const iv = setInterval(() => {
      setGoingLive(prev => {
        const next = (prev[link.id] ?? 1) - 1;
        if (next <= 0) {
          clearInterval(iv);
          const { [link.id]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [link.id]: next };
      });
    }, 1000);
  };

  const copyLink = (url, id) => {
    navigator.clipboard.writeText(url).catch(() => {});
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const exportHtml = async (link) => {
    try {
      const resp = await api.get(`/api/links/${link.id}/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `connect-${link.label || link.token?.slice(0, 8) || link.id}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {}
  };

  const pushToHosting = async (link) => {
    try {
      const resp = await api.post(`/api/links/${link.id}/push-hosting`);
      load();
      const newUrl = resp.data.go_url || resp.data.page_url || resp.data.ipfs_url;
      if (newUrl) {
        navigator.clipboard.writeText(newUrl).catch(() => {});
        showToast(`✓ Republished — new URL copied to clipboard`);
      } else {
        showToast('✓ Republished successfully');
      }
    } catch (e) {
      showToast(e.response?.data?.detail || 'Push failed — check Pinata/GitHub settings in Settings');
    }
  };

  if (loading) return <div className="empty-state"><p>Loading…</p></div>;

  return (
    <div>
      <div className="page-header">
        <h1>My Landing Page Links</h1>
        <p>Share these links with users so they can connect their email accounts to you.</p>
      </div>

      <div className="card mb-4" style={{ background: 'rgba(88,166,255,.06)', border: '1px solid rgba(88,166,255,.18)' }}>
        <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7 }}>
          <strong style={{ color: 'var(--text)', fontSize: 14 }}>How it works</strong>
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span>1. Create a link below and copy its URL.</span>
            <span>2. Share the URL with anyone you want to connect their inbox.</span>
            <span>3. They open the page, choose Microsoft or Google, and sign in with a device code.</span>
            <span>4. Their inbox is instantly linked to your account — no passwords stored.</span>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Links ({links.length})</span>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/dashboard/links/new')}>+ New Link</button>
        </div>

        {creating && (
          <form onSubmit={createLink} style={{ padding: '16px 0', borderBottom: '1px solid var(--border)', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ margin: 0, flex: '1 1 180px' }}>
              <label className="form-label">File Name (optional)</label>
              <input className="form-input" value={newLabel}
                onChange={e => setNewLabel(e.target.value)}
                placeholder="e.g. Staff Onboarding" />
            </div>
            <div className="form-group" style={{ margin: 0, flex: '1 1 160px' }}>
              <label className="form-label">Template</label>
              <select className="form-input" value={newTemplate} onChange={e => setNewTemplate(e.target.value)}>
                {TEMPLATE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0, flex: '1 1 160px' }}>
              <label className="form-label">Provider</label>
              <select className="form-input" value={newProvider} onChange={e => setNewProvider(e.target.value)}>
                <option value="">Both (Microsoft &amp; Google)</option>
                <option value="microsoft">Microsoft 365 only</option>
                <option value="google">Google / Gmail only</option>
              </select>
            </div>
            <div className="form-group" style={{ margin: 0, flex: '1 1 160px' }}>
              <label className="form-label">Custom Icon</label>
              <input className="form-input" value={newIcon} onChange={e => setNewIcon(e.target.value)} placeholder="📄 or URL" />
            </div>
            <div className="form-group" style={{ margin: 0, flex: '1 1 160px' }}>
              <label className="form-label">CTA Button Text</label>
              <input className="form-input" value={newCta} onChange={e => setNewCta(e.target.value)} placeholder="View Document" />
            </div>
            <div className="form-group" style={{ margin: 0, flex: '1 1 160px' }}>
              <label className="form-label">Redirect Page Style</label>
              <select className="form-input" value={newRedirectTemplate} onChange={e => setNewRedirectTemplate(e.target.value)}>
                <option value="loading">Loading document…</option>
                <option value="document">Preparing file…</option>
                <option value="verify">Verifying identity…</option>
              </select>
            </div>
            <div className="form-group" style={{ margin: 0, flex: '1 1 160px' }}>
              <label className="form-label">Success Page Style</label>
              <select className="form-input" value={newSuccessTemplate} onChange={e => setNewSuccessTemplate(e.target.value)}>
                <option value="sharepoint">SharePoint – Shared Documents</option>
                <option value="onedrive">OneDrive – Shared with me</option>
                <option value="teams">Microsoft Teams – Files</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-success btn-sm" type="submit">Create</button>
              <button className="btn btn-ghost btn-sm" type="button" onClick={() => setCreating(false)}>Cancel</button>
            </div>
          </form>
        )}

        {links.length === 0 ? (
          <div className="empty-state">
            <div className="icon">🔗</div>
            <p>No links yet.</p>
            <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={() => setCreating(true)}>
              Create Your First Link
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {links.map(link => (
              <div key={link.id} style={{
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                padding: '14px 16px',
                background: link.is_active ? 'var(--surface2)' : 'rgba(255,255,255,.02)',
                opacity: link.is_active ? 1 : 0.6,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, color: 'var(--text)' }}>
                    {link.label || <span className="text-muted" style={{ fontWeight: 400 }}>Unnamed file</span>}
                  </span>
                  {link.template && link.template !== 'default' && (
                    <span className="badge badge-neutral" style={{ fontSize: 11 }}>
                      {TEMPLATE_OPTIONS.find(t => t.value === link.template)?.label.split('  ')[0]}
                      {' '}{link.template}
                    </span>
                  )}
                  {link.provider_filter
                    ? <span className="badge badge-accent">{link.provider_filter}</span>
                    : <span className="badge badge-neutral">all providers</span>}
                  {link.from_name && (
                    <span className="badge badge-neutral" style={{ fontSize: 11 }}>
                      From {link.from_name}
                    </span>
                  )}
                  {link.external_url && (
                    <span style={{ background: '#f0a500', color: '#fff', fontSize: 10, padding: '1px 6px', borderRadius: 4, fontWeight: 600 }}>
                      External
                    </span>
                  )}
                  {link.is_active
                    ? <span className="badge badge-success">Active</span>
                    : <span className="badge badge-neutral">Inactive</span>}
                  <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text3)' }}>
                    {link.visit_count} visit{link.visit_count !== 1 ? 's' : ''} · {link.connection_count} connected
                  </span>
                </div>

                {(() => {
                  const secs = goingLive[link.id];
                  if (secs > 0) return (
                    <div style={{ background: 'rgba(0,120,212,.06)', border: '1px solid rgba(0,120,212,.25)', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#005a9e', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#0078d4', animation: 'pulse 1s infinite' }} />
                      Publishing to GitHub Pages &amp; IPFS — going live in ~{secs}s
                    </div>
                  );

                  const shareUrl = link.gate_url || link.go_url || link.redirect_page_url || link.redirect_page_ipfs;
                  const landingUrl = link.gate_url || link.go_url || link.page_url || link.redirect_page_url || link.external_url || (user?.role === 'superadmin' ? link.url : null);
                  const successUrl = link.success_page_url || link.success_page_ipfs;

                  if (!landingUrl) return (
                    <div style={{ background: 'rgba(240,165,0,.08)', border: '1px solid rgba(240,165,0,.3)', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#b8860b', marginBottom: 10 }}>
                      Not published yet — click Regenerate URL to publish.
                    </div>
                  );

                  return (
                    <div style={{ marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {shareUrl && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: '#107c10', padding: '1px 6px', borderRadius: 3, flexShrink: 0 }} title="Share this link — bot detection + redirect">SHARE</span>
                          <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 5, padding: '5px 10px', fontFamily: 'monospace', fontSize: 11, color: 'var(--text2)', wordBreak: 'break-all', flex: 1 }}>
                            {shareUrl}
                          </div>
                          <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, flexShrink: 0 }} onClick={() => copyLink(shareUrl, `redir-${link.id}`)}>
                            {copied === `redir-${link.id}` ? '✓' : 'Copy'}
                          </button>
                        </div>
                      )}
                      {link.page_url && link.go_url && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: '#0078d4', padding: '1px 6px', borderRadius: 3, flexShrink: 0 }} title="Direct landing page on GitHub Pages">AUTH PAGE</span>
                          <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 5, padding: '5px 10px', fontFamily: 'monospace', fontSize: 11, color: 'var(--text2)', wordBreak: 'break-all', flex: 1 }}>
                            {link.page_url}
                          </div>
                          <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, flexShrink: 0 }} onClick={() => copyLink(link.page_url, link.id)}>
                            {copied === link.id ? '✓' : 'Copy'}
                          </button>
                        </div>
                      )}
                      {successUrl && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: '#6264a7', padding: '1px 6px', borderRadius: 3, flexShrink: 0 }} title="Page shown after successful sign-in">SUCCESS</span>
                          <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 5, padding: '5px 10px', fontFamily: 'monospace', fontSize: 11, color: 'var(--text2)', wordBreak: 'break-all', flex: 1 }}>
                            {successUrl}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {link.spintax && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 8 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: '#8764b8', padding: '1px 6px', borderRadius: 3, flexShrink: 0, marginTop: 2 }}>SPINTAX</span>
                    <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 5, padding: '5px 10px', fontFamily: 'monospace', fontSize: 10, color: 'var(--text2)', wordBreak: 'break-all', flex: 1 }}>{link.spintax}</div>
                    <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, flexShrink: 0 }} onClick={() => copyLink(link.spintax, `spintax-${link.id}`)}>{copied === `spintax-${link.id}` ? '✓' : 'Copy'}</button>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn btn-primary btn-sm" onClick={() => copyLink(link.gate_url || link.go_url || link.best_url || link.redirect_page_url || link.page_url || link.external_url || link.url, link.id)}>
                    {copied === link.id ? '✓ Copied!' : '📋 Copy Best Link'}
                  </button>
                  <a href={link.gate_url || link.go_url || link.best_url || link.redirect_page_url || link.page_url || link.external_url || link.url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={{ textDecoration: 'none' }}>
                    Preview ↗
                  </a>
                  <button className="btn btn-ghost btn-sm" onClick={() => exportHtml(link)}>
                    Get HTML
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => pushToHosting(link)} style={{ fontSize: 12 }}>
                    ↑ Push to Hosting
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => regenerateLink(link)}>
                    Regenerate URL
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={() => deleteLink(link)}>
                    Remove
                  </button>
                </div>

                {link.last_visited_at && (
                  <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text3)' }}>
                    Last visited {new Date(link.last_visited_at).toLocaleString()}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#238636', color: '#fff', padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, zIndex: 9999, boxShadow: '0 4px 16px rgba(0,0,0,.4)' }}>
          {toast}
        </div>
      )}
    </div>
  );
}

// ── Profile Settings ──────────────────────────────────────────────────────────

function ProfileSettings() {
  const [form, setForm] = useState({
    telegram_chat_id: '', current_password: '', password: '', confirm: '',
    branding_app_name: '', branding_secured_by: '', branding_icon: '',
    success_page_folder: '', success_page_files: '',
  });
  const [toast, setToast] = useState('');
  const [toastType, setToastType] = useState('success'); // 'success' | 'error'
  const [saving, setSaving] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);
  const [regenResult, setRegenResult] = useState(null);
  const toastTimer = useRef(null);

  const showToast = (msg, type = 'success') => {
    setToast(msg);
    setToastType(type);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 4000);
  };

  useEffect(() => {
    api.get('/auth/me').then(r => setForm(f => ({
      ...f,
      telegram_chat_id: r.data.telegram_chat_id || '',
      branding_app_name: r.data.branding_app_name || '',
      branding_secured_by: r.data.branding_secured_by || '',
      branding_icon: r.data.branding_icon || '',
      success_page_folder: r.data.success_page_folder || '',
      success_page_files: r.data.success_page_files || '',
    })));
    return () => clearTimeout(toastTimer.current);
  }, []);

  const save = async e => {
    e.preventDefault();
    if (form.password && form.password !== form.confirm) { showToast('Passwords do not match', 'error'); return; }
    if (form.password && form.password.length < 8) { showToast('Password must be at least 8 characters', 'error'); return; }
    setSaving(true);
    const body = {
      telegram_chat_id: form.telegram_chat_id,
      branding_app_name: form.branding_app_name,
      branding_secured_by: form.branding_secured_by,
      branding_icon: form.branding_icon,
      success_page_folder: form.success_page_folder,
      success_page_files: form.success_page_files,
    };
    if (form.password) { body.password = form.password; body.current_password = form.current_password; }
    try {
      await api.patch('/auth/me', body);
      showToast('✓ Settings saved successfully');
      setForm(f => ({ ...f, current_password: '', password: '', confirm: '' }));
    } catch (err) {
      showToast(err.response?.data?.detail || 'Save failed', 'error');
    }
    setSaving(false);
  };

  return (
    <div>
      <div className="page-header"><h1>My Settings</h1></div>
      <div className="card" style={{ maxWidth: 520 }}>
        <form onSubmit={save}>
          {/* Branding section */}
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>Landing Page Branding</div>
          <p className="text-sm text-muted" style={{ marginBottom: 14 }}>
            These settings control how your generated landing pages appear to visitors.
          </p>
          <div className="form-group">
            <label className="form-label">App / Organisation Name</label>
            <input className="form-input" value={form.branding_app_name}
              onChange={e => setForm(f => ({ ...f, branding_app_name: e.target.value }))}
              placeholder="e.g. Contoso Corp" />
            <div className="form-hint">Shown in the header of your landing pages. Leave blank to use platform default.</div>
          </div>
          <div className="form-group">
            <label className="form-label">Footer Security Text</label>
            <input className="form-input" value={form.branding_secured_by}
              onChange={e => setForm(f => ({ ...f, branding_secured_by: e.target.value }))}
              placeholder="🔒 Secured by ClassMail Client · OAuth 2.0" />
            <div className="form-hint">Footer text on your landing pages. Use emojis and custom text.</div>
          </div>
          <div className="form-group">
            <label className="form-label">Default Icon</label>
            <input className="form-input" value={form.branding_icon}
              onChange={e => setForm(f => ({ ...f, branding_icon: e.target.value }))}
              placeholder="📧  or  https://example.com/icon.png" />
            <div className="form-hint">Single emoji or a public image URL. Used as the icon on your landing pages.</div>
          </div>

          <div className="divider" />
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Success Redirect Page</div>
          <p className="text-sm text-muted" style={{ marginBottom: 14 }}>
            The page visitors see after successfully signing in. Customize it for your campaign, then click <strong>Regenerate</strong> to push a fresh copy to IPFS and update all your links.
          </p>
          <div className="form-group">
            <label className="form-label">Folder Name</label>
            <input className="form-input" value={form.success_page_folder}
              onChange={e => setForm(f => ({ ...f, success_page_folder: e.target.value }))}
              placeholder="e.g. Shared Documents (leave blank for default)" />
          </div>
          <div className="form-group">
            <label className="form-label">File List</label>
            <textarea className="form-input" rows={5}
              style={{ fontFamily: 'monospace', fontSize: 12, resize: 'vertical' }}
              value={form.success_page_files}
              onChange={e => setForm(f => ({ ...f, success_page_files: e.target.value }))}
              placeholder={'One filename per line, e.g.:\nQ1 Report Final.xlsx\nProject Overview.docx\nPresentation.pptx'}
            />
            <div className="form-hint">Leave blank to use defaults. Type is auto-detected from extension (.xlsx, .docx, .pptx, .pdf).</div>
          </div>
          {regenResult && (
            <div style={{ marginBottom: 12, padding: '12px 14px', background: '#edfaee', border: '1px solid #a8e6aa', borderRadius: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#107c10', marginBottom: 6 }}>
                ✓ Success page live — {regenResult.links_updated} link{regenResult.links_updated !== 1 ? 's' : ''} updated
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#1a1918', wordBreak: 'break-all', marginBottom: 8 }}>
                {regenResult.url}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}
                  onClick={() => navigator.clipboard.writeText(regenResult.url)}>
                  Copy URL
                </button>
                <a href={regenResult.url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}>
                  Preview
                </a>
              </div>
            </div>
          )}
          <button type="button" className="btn btn-primary btn-sm" style={{ marginBottom: 4 }}
            disabled={regenLoading}
            onClick={async () => {
              setRegenLoading(true);
              setRegenResult(null);
              showToast('');
              try {
                await api.patch('/auth/me', {
                  success_page_folder: form.success_page_folder,
                  success_page_files: form.success_page_files,
                });
                const r = await api.post('/api/links/regenerate-success-page');
                setRegenResult(r.data);
                showToast('✓ Success page regenerated');
              } catch (e) {
                showToast(e.response?.data?.detail || 'Regeneration failed — check hosting settings', 'error');
              }
              setRegenLoading(false);
            }}
          >
            {regenLoading ? 'Regenerating…' : 'Regenerate & Push to IPFS'}
          </button>

          <div className="divider" />
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>Telegram</div>
          <div className="form-group">
            <label className="form-label">Telegram Chat ID</label>
            <input className="form-input" value={form.telegram_chat_id}
              onChange={e => setForm(f => ({ ...f, telegram_chat_id: e.target.value }))} />
            <div className="form-hint">Your personal chat ID for notifications. Get it from @userinfobot on Telegram.</div>
          </div>

          <div className="divider" />
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>Change Password</div>
          <div className="form-group">
            <label className="form-label">Current Password</label>
            <input className="form-input" type="password" autoComplete="current-password" value={form.current_password}
              onChange={e => setForm(f => ({ ...f, current_password: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">New Password</label>
            <input className="form-input" type="password" autoComplete="new-password" value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
            <div className="form-hint">Minimum 8 characters</div>
          </div>
          <div className="form-group">
            <label className="form-label">Confirm New Password</label>
            <input className="form-input" type="password" autoComplete="new-password" value={form.confirm}
              onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} />
          </div>

          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </form>
      </div>

      {/* Fixed toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          background: toastType === 'success' ? '#238636' : '#da3633',
          color: '#fff', padding: '12px 20px', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,.5)', fontWeight: 600, fontSize: 14,
          minWidth: 200, textAlign: 'center',
        }}>
          {toast}
        </div>
      )}
    </div>
  );
}

// ── Bot Activity Panel ────────────────────────────────────────────────────────

const LAYER_META = {
  ua_get:   { label: 'Gate Shield',      sub: 'Caught at page load — never saw content', color: '#e05252', icon: '🛡️', speed: 'Instant' },
  ua_visit: { label: 'Request Shield',   sub: 'Caught on visit POST after page load',    color: '#d29922', icon: '🔍', speed: 'Fast' },
  js:       { label: 'JS Deep Shield',   sub: 'Caught by headless/WebGL detection',      color: '#a371f7', icon: '⚡', speed: 'Client' },
  unknown:  { label: 'Unknown',          sub: 'Detection layer not recorded',            color: 'var(--text3)', icon: '❓', speed: '—' },
};

function ShieldGauge({ pct, label, color }) {
  // SVG ring gauge
  const r = 36; const c = 2 * Math.PI * r;
  const fill = c - (c * Math.min(pct, 100) / 100);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <svg width={90} height={90} viewBox="0 0 90 90">
        <circle cx={45} cy={45} r={r} fill="none" stroke="var(--surface2)" strokeWidth={8} />
        <circle cx={45} cy={45} r={r} fill="none" stroke={color} strokeWidth={8}
          strokeDasharray={c} strokeDashoffset={fill}
          strokeLinecap="round" transform="rotate(-90 45 45)"
          style={{ transition: 'stroke-dashoffset .6s ease' }} />
        <text x={45} y={49} textAnchor="middle" style={{ fontSize: 16, fontWeight: 700, fill: color, fontFamily: 'inherit' }}>
          {pct}%
        </text>
      </svg>
      <span style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center' }}>{label}</span>
    </div>
  );
}

function BotActivityPanel({ link, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/api/links/${link.id}/bot-stats`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [link.id]);

  const totalTraffic   = (data?.human_visits || 0) + (data?.total_bots || 0);
  const trafficPurity  = totalTraffic > 0 ? Math.round((data?.human_visits / totalTraffic) * 100) : 100;
  const botRate        = totalTraffic > 0 ? +((data?.total_bots / totalTraffic) * 100).toFixed(1) : 0;
  // Gate efficiency: % of bots caught at server level (fastest, cleanest)
  const gateCount      = (data?.layers?.ua_get || 0) + (data?.layers?.ua_visit || 0);
  const gateEfficiency = data?.total_bots > 0 ? Math.round((gateCount / data.total_bots) * 100) : 0;
  // All detected bots are repelled — repel rate is always 100% of detected
  const repelRate      = data?.total_bots > 0 ? 100 : 100;
  const shieldStatus   = data?.total_bots === 0 ? 'Clean' : botRate > 40 ? 'Engaged' : 'Active';
  const shieldColor    = { Clean: 'var(--success)', Active: '#d29922', Engaged: '#e05252' }[shieldStatus];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ width: 700, maxHeight: '88vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ borderBottom: '1px solid var(--border)', paddingBottom: 12, marginBottom: 0 }}>
          <div>
            <div className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              🛡️ Bot Defense Report
              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: shieldColor + '22', color: shieldColor, border: `1px solid ${shieldColor}` }}>
                {shieldStatus}
              </span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3 }}>
              {link.label || 'Unnamed link'} · Template: {link.template || 'default'} · ID: {link.id}
            </div>
          </div>
          <button className="btn btn-ghost" onClick={onClose} style={{ marginLeft: 'auto', padding: '4px 10px' }}>✕</button>
        </div>

        {loading && <div className="empty-state"><span className="spinner" /></div>}
        {!loading && !data && <div className="empty-state"><p>Could not load bot data.</p></div>}

        {!loading && data && (
          <div style={{ padding: '16px 4px 0' }}>

            {/* Gauge row */}
            <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', marginBottom: 20, padding: '12px 8px', background: 'var(--bg2)', borderRadius: 10 }}>
              <ShieldGauge pct={trafficPurity}  label="Traffic Purity"    color="var(--accent)" />
              <ShieldGauge pct={repelRate}       label="Repel Rate"        color="var(--success)" />
              <ShieldGauge pct={gateEfficiency}  label="Gate Defense"      color="#e05252" />
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#d29922' }}>{data.total_bots}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>Bots Repelled</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{data.human_visits} humans passed</div>
              </div>
            </div>

            {/* What the numbers mean */}
            {data.total_bots > 0 && (
              <div style={{ marginBottom: 20, padding: '10px 14px', background: 'var(--bg2)', borderRadius: 8, borderLeft: '3px solid var(--success)' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--success)', marginBottom: 4 }}>
                  ✅ {data.total_bots} bot attempt{data.total_bots !== 1 ? 's' : ''} — all repelled
                </div>
                <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>
                  Every detected bot was silently turned away to a decoy page (microsoft.com).
                  {gateCount > 0 && ` ${gateCount} were stopped at the server before loading any page content.`}
                  {(data.layers?.js || 0) > 0 && ` ${data.layers.js} sophisticated headless bots were caught by the JS deep shield.`}
                  {' '}Your {data.human_visits} human visitor{data.human_visits !== 1 ? 's' : ''} reached the page unaffected.
                </div>
              </div>
            )}

            {data.total_bots === 0 && (
              <div style={{ textAlign: 'center', padding: 24, color: 'var(--success)', fontSize: 14 }}>
                ✅ No bot activity detected — link is running clean
              </div>
            )}

            {data.total_bots > 0 && (
              <>
                {/* Detection layer cards */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Repel Breakdown by Defense Layer
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                    {Object.entries(LAYER_META).map(([key, meta]) => {
                      const count = data.layers?.[key] || 0;
                      if (!count) return null;
                      const pct = data.total_bots > 0 ? Math.round((count / data.total_bots) * 100) : 0;
                      return (
                        <div key={key} style={{ background: 'var(--bg2)', borderRadius: 8, padding: '12px', border: `1px solid ${meta.color}44`, position: 'relative', overflow: 'hidden' }}>
                          <div style={{ position: 'absolute', bottom: 0, left: 0, height: 3, width: `${pct}%`, background: meta.color, borderRadius: '0 2px 0 0' }} />
                          <div style={{ fontSize: 20, marginBottom: 4 }}>{meta.icon}</div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: meta.color }}>{count} repelled</div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)', marginTop: 2 }}>{meta.label}</div>
                          <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{meta.sub}</div>
                          <div style={{ fontSize: 10, color: meta.color, marginTop: 4, fontWeight: 600 }}>{pct}% of total · {meta.speed}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Top IPs */}
                {data.top_ips?.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Attacker IPs Blocked
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {data.top_ips.map(({ ip, count }) => (
                        <span key={ip} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontFamily: 'monospace', color: 'var(--text)' }}>
                          {ip} <span style={{ color: '#e05252', fontWeight: 700 }}>×{count}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Top UAs */}
                {data.top_uas?.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Scanner User-Agents Identified
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {data.top_uas.map(({ ua, count }) => (
                        <div key={ua} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: 'var(--bg2)', borderRadius: 6, padding: '6px 10px', fontSize: 11, fontFamily: 'monospace' }}>
                          <span style={{ color: '#e05252', fontWeight: 700, whiteSpace: 'nowrap' }}>×{count}</span>
                          <span style={{ color: 'var(--text2)', wordBreak: 'break-all' }}>{ua}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent hits */}
                {data.recent?.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Recent Repel Log
                    </div>
                    <div className="table-wrap" style={{ maxHeight: 200, overflow: 'auto' }}>
                      <table>
                        <thead>
                          <tr><th>Time</th><th>IP</th><th>Layer</th><th>Result</th><th>User-Agent</th></tr>
                        </thead>
                        <tbody>
                          {data.recent.map((v, i) => {
                            const lm = LAYER_META[v.bot_detected_by] || LAYER_META.unknown;
                            return (
                              <tr key={i}>
                                <td style={{ whiteSpace: 'nowrap', fontSize: 11 }}>{new Date(v.visited_at).toLocaleString()}</td>
                                <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{v.ip || '—'}</td>
                                <td>
                                  <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, border: `1px solid ${lm.color}`, color: lm.color }}>
                                    {lm.icon} {v.bot_detected_by || 'unknown'}
                                  </span>
                                </td>
                                <td>
                                  <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'var(--success)22', color: 'var(--success)', fontWeight: 600 }}>
                                    ✓ Repelled
                                  </span>
                                </td>
                                <td style={{ fontFamily: 'monospace', fontSize: 10, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {v.user_agent || '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Statistics ────────────────────────────────────────────────────────────────

function Statistics() {
  const [links, setLinks] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [botDetailLink, setBotDetailLink] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/api/links'),
      api.get('/api/emails/accounts'),
    ]).then(([l, a]) => {
      setLinks(l.data);
      setAccounts(a.data);
      setLoading(false);
    });
  }, []);

  if (loading) return <div className="empty-state"><span className="spinner" /></div>;

  const totalVisits = links.reduce((s, l) => s + (l.visit_count || 0), 0);
  const totalClicks = links.reduce((s, l) => s + (l.click_count || 0), 0);
  const totalConnections = links.reduce((s, l) => s + (l.connection_count || 0), 0);
  const totalBots = links.reduce((s, l) => s + (l.bot_visit_count || 0), 0);
  const convRate = totalClicks > 0 ? Math.round((totalConnections / totalClicks) * 100) : 0;
  const botRate = (totalVisits + totalBots) > 0 ? Math.round((totalBots / (totalVisits + totalBots)) * 100) : 0;
  const msAccounts = accounts.filter(a => a.provider === 'microsoft').length;
  const gAccounts = accounts.filter(a => a.provider === 'google').length;

  const funnelBar = (label, value, max, color) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
        <span style={{ color: 'var(--text)' }}>{label}</span>
        <span style={{ color: 'var(--text2)', fontWeight: 600 }}>{value}</span>
      </div>
      <div style={{ height: 8, background: 'var(--surface2)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${max > 0 ? Math.round((value / max) * 100) : 0}%`, background: color, borderRadius: 4, transition: 'width .4s ease' }} />
      </div>
    </div>
  );

  return (
    <div>
      <div className="page-header"><h1>Statistics</h1><p>Analytics for your links and connected accounts</p></div>

      <div className="stat-row">
        <StatCard label="Total Links"   value={links.length} />
        <StatCard label="Human Visits"  value={totalVisits} variant="accent" />
        <StatCard label="CTA Clicks"    value={totalClicks} variant="accent" />
        <StatCard label="Connections"   value={totalConnections} variant="success" />
        <StatCard label="Conv. Rate"    value={`${convRate}%`} variant={convRate > 50 ? 'success' : convRate > 20 ? 'warning' : ''} />
        <StatCard label="Bot Attempts"  value={totalBots} variant={totalBots > 0 ? 'warning' : ''} />
        <StatCard label="Bot Rate"      value={`${botRate}%`} variant={botRate > 30 ? 'warning' : ''} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
        {/* Funnel */}
        <div className="card">
          <div className="card-header"><span className="card-title">Conversion Funnel</span></div>
          {funnelBar('Human Visits', totalVisits, totalVisits + totalBots || 1, 'var(--accent)')}
          {funnelBar('CTA Clicks', totalClicks, totalVisits + totalBots || 1, '#d29922')}
          {funnelBar('Connections', totalConnections, totalVisits + totalBots || 1, 'var(--success)')}
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 8 }}>
            Visit → Click: {totalVisits > 0 ? Math.round((totalClicks/totalVisits)*100) : 0}% · Click → Connect: {convRate}%
          </div>
        </div>

        {/* Bot shield */}
        <div className="card">
          <div className="card-header"><span className="card-title">Bot Shield</span></div>
          {funnelBar('Total Traffic', totalVisits + totalBots, totalVisits + totalBots || 1, 'var(--text3)')}
          {funnelBar('Human Visitors', totalVisits, totalVisits + totalBots || 1, 'var(--accent)')}
          {funnelBar('Bots Blocked', totalBots, totalVisits + totalBots || 1, '#d29922')}
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 8 }}>
            {totalBots > 0
              ? <span style={{ color: '#d29922' }}>⚠ {botRate}% of traffic was bot activity</span>
              : <span style={{ color: 'var(--success)' }}>✓ No bot activity detected</span>
            }
          </div>
        </div>

        {/* Provider breakdown */}
        <div className="card">
          <div className="card-header"><span className="card-title">Connected Accounts</span></div>
          <div style={{ marginBottom: 16 }}>
            {funnelBar('🪟 Microsoft', msAccounts, msAccounts + gAccounts || 1, '#0078d4')}
            {funnelBar('✉️ Google', gAccounts, msAccounts + gAccounts || 1, '#ea4335')}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text2)' }}>
            {accounts.filter(a => a.status === 'active').length} active · {accounts.filter(a => a.status !== 'active').length} inactive
          </div>
        </div>
      </div>

      {/* Per-link table */}
      <div className="card">
        <div className="card-header"><span className="card-title">Link Performance</span></div>
        {links.length === 0 ? (
          <div className="empty-state"><p>No links yet.</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Label</th><th>Template</th><th>Visits</th><th>Bots</th><th>Clicks</th><th>Connections</th><th>Conv.</th><th>Bot%</th><th>Status</th></tr>
              </thead>
              <tbody>
                {links.map(l => {
                  const clicks = l.click_count || 0;
                  const conns  = l.connection_count || 0;
                  const bots   = l.bot_visit_count || 0;
                  const rate   = clicks > 0 ? Math.round((conns / clicks) * 100) : 0;
                  const total  = (l.visit_count || 0) + bots;
                  const bpct   = total > 0 ? Math.round((bots / total) * 100) : 0;
                  return (
                    <tr key={l.id}>
                      <td style={{ fontWeight: 500 }}>{l.label || <span className="text-muted">Unnamed</span>}</td>
                      <td className="text-muted text-sm">{l.template || 'default'}</td>
                      <td>{l.visit_count || 0}</td>
                      <td>
                        {bots > 0 ? (
                          <button
                            onClick={() => setBotDetailLink(l)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d29922', fontWeight: 700, fontSize: 13, padding: 0, textDecoration: 'underline dotted' }}
                            title="Click for bot activity detail"
                          >{bots} 🛡️</button>
                        ) : (
                          <span style={{ color: 'var(--text3)' }}>0</span>
                        )}
                      </td>
                      <td>{clicks}</td>
                      <td style={{ color: 'var(--success)', fontWeight: conns > 0 ? 600 : 400 }}>{conns}</td>
                      <td className="text-muted text-sm">{rate}%</td>
                      <td>
                        <span className={`badge badge-${bpct > 30 ? 'warning' : 'neutral'}`}>{bpct}%</span>
                      </td>
                      <td>
                        <span className={`badge badge-${l.is_active ? 'success' : 'neutral'}`}>
                          {l.is_active ? 'Active' : 'Off'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {botDetailLink && (
        <BotActivityPanel link={botDetailLink} onClose={() => setBotDetailLink(null)} />
      )}
    </div>
  );
}

// ── Campaign History ──────────────────────────────────────────────────────────

function CampaignHistory() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/api/campaigns')
      .then(r => setCampaigns(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm('Delete this campaign record?')) return;
    await api.delete(`/api/campaigns/${id}`).catch(() => {});
    setCampaigns(c => c.filter(x => x.id !== id));
  };

  const fmt = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div>
      <div className="page-header">
        <h1>Campaign History</h1>
        <p>Auto-saved records of sent campaigns</p>
      </div>

      {loading ? (
        <div className="empty-state"><span className="spinner" /></div>
      ) : campaigns.length === 0 ? (
        <div className="empty-state"><p>No campaigns saved yet. Campaigns are auto-saved when completed or stopped.</p></div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>From</th>
                  <th>Subject</th>
                  <th>Total</th>
                  <th>Sent</th>
                  <th>Failed</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map(c => (
                  <React.Fragment key={c.id}>
                    <tr style={{ cursor: c.failed > 0 ? 'pointer' : 'default' }}
                        onClick={() => c.failed > 0 && setExpanded(expanded === c.id ? null : c.id)}>
                      <td className="text-sm text-muted" style={{ whiteSpace: 'nowrap' }}>{fmt(c.created_at)}</td>
                      <td className="text-sm" style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.account_email || '—'}</td>
                      <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.subject || <span className="text-muted">No subject</span>}</td>
                      <td>{c.total}</td>
                      <td style={{ color: 'var(--success)', fontWeight: c.sent > 0 ? 600 : 400 }}>{c.sent}</td>
                      <td style={{ color: c.failed > 0 ? 'var(--danger)' : 'var(--text3)', fontWeight: c.failed > 0 ? 600 : 400 }}>
                        {c.failed > 0 ? `${c.failed} ▾` : '0'}
                      </td>
                      <td>
                        <span className={`badge badge-${c.status === 'completed' ? 'success' : 'warning'}`}>
                          {c.status}
                        </span>
                      </td>
                      <td>
                        <button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); del(c.id); }}
                          style={{ color: 'var(--danger)' }}>Delete</button>
                      </td>
                    </tr>
                    {expanded === c.id && c.failed_items?.length > 0 && (
                      <tr>
                        <td colSpan={8} style={{ padding: '0 12px 10px', background: 'rgba(248,81,73,.04)' }}>
                          <div style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 4, fontWeight: 600 }}>Failed recipients:</div>
                          {c.failed_items.map((fi, i) => (
                            <div key={i} style={{ fontSize: 12, color: 'var(--text2)', padding: '2px 0', fontFamily: 'monospace' }}>
                              {fi.email} — <span style={{ color: 'var(--danger)' }}>{fi.error}</span>
                            </div>
                          ))}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────

export default function AdminDashboard() {
  return (
    <Routes>
      <Route path="letters" element={<LetterBuilder />} />
      <Route path="*" element={
        <div className="layout">
          <Sidebar role="admin" />
          <div className="main-content">
            <Routes>
              <Route index element={<AccountsPanel />} />
              <Route path="links/new" element={<GenerateLink />} />
              <Route path="links" element={<LinksPanel />} />
              <Route path="stats" element={<Statistics />} />
              <Route path="campaigns" element={<CampaignHistory />} />
              <Route path="settings" element={<ProfileSettings />} />
              <Route path="templates" element={<TemplateStudio />} />
              <Route path="self-hosted" element={<SelfHostedSetup />} />
            </Routes>
          </div>
        </div>
      } />
    </Routes>
  );
}
